<template>
  <div class="family-categories">
    <p class="family-categories__intro">{{ $t('familyCategories.intro') }}</p>

    <div
      v-if="loading"
      class="family-categories__grid"
    >
      <div
        v-for="placeholder in 2"
        :key="placeholder"
        class="family-categories__skeleton"
      >
        <el-skeleton
          :rows="3"
          animated
        />
      </div>
    </div>

    <div
      v-else
      class="family-categories__grid"
    >
      <FamilyCategoryCard
        v-for="item in categoriesWithFamilies"
        :key="item.category.code"
        :label="item.label"
        :families="item.families"
        :category-label-by-family="categoryLabelByFamily"
        :can-manage="canManage"
        @add="(family) => addFamily(item, family)"
        @remove="(familyType) => removeFamily(item, familyType)"
        @rename="dialogCategory = item.category"
        @delete="deleteCategory(item)"
      />
      <button
        v-if="canManage"
        type="button"
        class="family-categories__new"
        @click="dialogCategory = null"
      >
        <span class="family-categories__new-icon"
          ><i class="fas fa-plus"
        /></span>
        {{ $t('familyCategories.newCategory') }}
      </button>
    </div>

    <FamilyCategoryDialog
      v-if="dialogCategory !== undefined"
      :category="dialogCategory"
      @close="dialogCategory = undefined"
      @saved="categorySaved"
    />
  </div>
</template>

<script>
  import { analysesApi } from '@/api';
  import { FamilyTypes } from '@/config/constants';
  import acl from '@/services/acl';
  import familyCategories from '@/services/familyCategories';
  import FamilyCategoryCard from './FamilyCategoryCard.vue';
  import FamilyCategoryDialog from './FamilyCategoryDialog.vue';

  export default {
    name: 'FamilyCategoriesIndex',
    components: {
      FamilyCategoryCard,
      FamilyCategoryDialog,
    },
    data() {
      return {
        loading: true,
        categories: [],
        familyTypes: [],
        // undefined: dialog closed; null: creating; a category: renaming it
        dialogCategory: undefined,
      };
    },
    computed: {
      canManage() {
        return acl.userCanUse(['familyTypeManage']);
      },
      categoriesWithFamilies() {
        return this.categories.map((category) => ({
          category,
          label: familyCategories.label(category),
          families: this.familyTypes.filter(
            (familyType) => familyType.category === category.code
          ),
        }));
      },
      // Lets a card tell, while searching, that a family already sits in another category
      categoryLabelByFamily() {
        return Object.fromEntries(
          this.categoriesWithFamilies.flatMap(({ label, families }) =>
            families.map((familyType) => [familyType.familyCode, label])
          )
        );
      },
    },
    created() {
      this.load();
    },
    methods: {
      async load() {
        try {
          const [{ data: categories }, { data: familyTypes }] =
            await Promise.all([
              analysesApi.familyCategories(),
              analysesApi.familyTypesAll(),
            ]);
          this.categories = categories;
          this.familyTypes = familyTypes;
        } catch (error) {
          this.notifyError(error);
        } finally {
          this.loading = false;
        }
      },
      async addFamily({ category, label }, family) {
        const existing = this.familyTypes.find(
          (familyType) => familyType.familyCode === family.familyCode
        );
        const previous = existing && existing.category;
        if (previous === category.code) return;
        if (previous) {
          const confirmed = await this.$confirm(
            this.$t('familyCategories.moveConfirm', {
              family: family.familyCode,
              category: this.categoryLabelByFamily[family.familyCode],
              target: label,
            }),
            this.$t('familyCategories.moveTitle'),
            {
              confirmButtonText: this.$t('familyCategories.move'),
              cancelButtonText: this.$t('familyCategories.cancel'),
              type: 'warning',
            }
          )
            .then(() => true)
            .catch(() => false);
          if (!confirmed) return;
        }
        if (
          await this.saveCategory(existing, family.familyCode, category.code)
        ) {
          this.notifySuccess(
            previous ? 'moved' : 'added',
            family.familyCode,
            label
          );
        }
      },
      async categorySaved(label) {
        const action = this.dialogCategory ? 'renamed' : 'created';
        this.dialogCategory = undefined;
        this.$message({
          type: 'success',
          message: this.$t(`familyCategories.${action}`, { category: label }),
        });
        await this.load();
      },
      async deleteCategory({ category, label, families }) {
        const confirmed = await this.$confirm(
          this.$tc('familyCategories.deleteConfirm', families.length, {
            category: label,
            count: families.length,
          }),
          this.$t('familyCategories.deleteTitle'),
          {
            confirmButtonText: this.$t('familyCategories.delete'),
            cancelButtonText: this.$t('familyCategories.cancel'),
            confirmButtonClass: 'el-button--danger',
            type: 'warning',
          }
        )
          .then(() => true)
          .catch(() => false);
        if (!confirmed) return;
        try {
          await analysesApi.familyCategoriesDelete(category.code);
          this.$message({
            type: 'success',
            message: this.$t('familyCategories.deleted', { category: label }),
          });
          await this.load();
        } catch (error) {
          this.notifyError(error);
        }
      },
      async removeFamily({ label }, familyType) {
        if (await this.saveCategory(familyType, familyType.familyCode, null)) {
          this.notifySuccess('removed', familyType.familyCode, label);
        }
      },
      // A family without a familytype row gets one, with the default report template.
      // Resolves to whether the change was saved.
      async saveCategory(existing, familyCode, category) {
        try {
          if (existing) {
            await analysesApi.familyTypesPatch(existing.id, {
              familyCode,
              type: existing.type,
              category,
            });
          } else {
            await analysesApi.familyTypesCreate({
              familyCode,
              type: FamilyTypes.Default,
              category,
            });
          }
          const { data } = await analysesApi.familyTypesAll();
          this.familyTypes = data;
          return true;
        } catch (error) {
          this.notifyError(error);
          return false;
        }
      },
      notifySuccess(action, family, category) {
        this.$message({
          type: 'success',
          message: this.$t(`familyCategories.${action}`, { family, category }),
        });
      },
      notifyError(error) {
        this.$notify({
          showClose: true,
          type: 'error',
          message: error.response.data.error.message,
        });
      },
    },
  };
</script>

<style lang="scss" scoped>
  .family-categories {
    &__intro {
      max-width: 720px;
      margin: -14px 0 32px;
      color: $--color-text-regular;
      font-size: 14px;
      line-height: 1.6;
    }

    &__grid {
      display: grid;
      grid-template-columns: repeat(auto-fill, minmax(380px, 1fr));
      gap: 24px;
      align-items: start;
    }

    // Placeholder card: a quiet invitation that brightens on hover
    &__new {
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 12px;
      min-height: 148px;
      padding: 24px;
      border: 1.5px dashed rgba(#fff, 0.14);
      border-radius: 16px;
      background: transparent;
      color: $--color-text-regular;
      font-size: 15px;
      font-weight: 600;
      cursor: pointer;
      transition: border-color 0.2s cubic-bezier(0.4, 0, 0.2, 1),
        background 0.2s cubic-bezier(0.4, 0, 0.2, 1),
        color 0.2s cubic-bezier(0.4, 0, 0.2, 1);

      &:hover,
      &:focus-visible {
        border-color: rgba($--color-primary, 0.6);
        background: rgba($--color-primary, 0.06);
        color: $--color-white;
        outline: none;
      }
    }

    &__new-icon {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: 32px;
      height: 32px;
      border-radius: 50%;
      background: rgba($--color-primary, 0.14);
      color: $--color-primary;
      font-size: 13px;
    }

    // Same retint as the monitoring dashboard: Element's skeleton grey glares on dark panels
    &__skeleton {
      padding: 24px;
      border: 1px solid rgba(#fff, 0.06);
      border-radius: 16px;
      background: #2e3454;

      ::v-deep .el-skeleton__item {
        background: rgba(#fff, 0.07);
      }

      ::v-deep .el-skeleton.is-animated .el-skeleton__item {
        background: linear-gradient(
          90deg,
          rgba(#fff, 0.07) 25%,
          rgba(#fff, 0.14) 37%,
          rgba(#fff, 0.07) 63%
        );
        background-size: 400% 100%;
      }
    }
  }
</style>
